import { GlobalOutlined, SearchOutlined } from "@ant-design/icons";
import { Input, Modal } from "antd";
import { useEffect, useState } from "react";
import styled from "styled-components";
import { Country } from "./Country";

const StyledModal = styled(Modal)`
  width: min(calc(100% - 24px), 600px) !important;

  .ant-modal-content {
    height: min(760px, calc(100dvh - 32px));
    padding: clamp(18px, 5vw, 28px);
    overflow: hidden;
    border-radius: clamp(18px, 5vw, 26px);
  }

  .ant-modal-body {
    height: 100%;
  }

  .ant-modal-close {
    top: clamp(18px, 5vw, 28px);
    right: clamp(18px, 5vw, 28px);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    color: #9299a5;
    background: #f2f3f5;
  }
`;

const ModalLayout = styled.div`
  height: 100%;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
`;

const Heading = styled.h2`
  max-width: calc(100% - 56px);
  margin: 0 0 clamp(18px, 5vw, 26px);
  font-family: "Diazo Cond Bold", Arial, sans-serif;
  font-size: clamp(25px, 7vw, 36px);
  line-height: 1.05;
`;

const Search = styled(Input)`
  height: clamp(58px, 16vw, 78px);
  border: 2px solid #e0e3e8;
  border-radius: 16px;
  font-family: "Bio Sans Regular", Arial, sans-serif;
  font-size: clamp(15px, 4vw, 19px);

  .ant-input-prefix {
    margin-right: 10px;
    color: #96a0af;
    font-size: 21px;
  }
`;

const CountryList = styled.div`
  min-height: 0;
  display: grid;
  gap: 11px;
  align-content: start;
  overflow-y: auto;
  margin-top: 18px;
  padding-right: 3px;
`;

const CountryCard = styled.article`
  min-height: clamp(82px, 23vw, 112px);
  display: flex;
  align-items: center;
  gap: clamp(13px, 4vw, 20px);
  padding: clamp(13px, 4vw, 20px);
  border: 2px solid #dedede;
  border-radius: 18px;
`;

const CountryIcon = styled.span`
  width: clamp(46px, 14vw, 64px);
  height: clamp(46px, 14vw, 64px);
  display: grid;
  place-items: center;
  flex: none;
  border-radius: 50%;
  color: #ffd947;
  background: #1248a6;
  font-size: clamp(22px, 6vw, 30px);
`;

const CountryName = styled.strong`
  display: block;
  font-family: "Bio Sans SemiBold", Arial, sans-serif;
  font-size: clamp(15px, 4.2vw, 20px);
`;

const Operators = styled.span`
  display: block;
  margin-top: 6px;
  color: #747c8d;
  font-size: clamp(12px, 3.3vw, 16px);
  line-height: 1.4;
`;

const NoResults = styled.p`
  padding: 24px 0;
  color: #6e7480;
  text-align: center;
`;

export default function CountryListModal(props: {
  countries: Country[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!props.isOpen) setSearch("");
  }, [props.isOpen]);

  const query = search.trim().toLowerCase();
  const filteredCountries = props.countries.filter((country) =>
    [country.name, ...country.telcos].join(" ").toLowerCase().includes(query)
  );

  return (
    <StyledModal
      open={props.isOpen}
      onCancel={() => props.setIsOpen(false)}
      footer={null}
      centered
      destroyOnClose
      aria-labelledby="coverage-modal-title"
    >
      <ModalLayout>
        <Heading id="coverage-modal-title">
          Coverage &amp; networks ({props.countries.length} countries)
        </Heading>
        <Search
          prefix={<SearchOutlined />}
          placeholder="Search country or operator…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          allowClear
          autoFocus
        />
        <CountryList>
          {filteredCountries.map((country) => (
            <CountryCard key={country.name}>
              <CountryIcon><GlobalOutlined /></CountryIcon>
              <div>
                <CountryName>{country.name}</CountryName>
                <Operators>{country.telcos.length ? country.telcos.join(" · ") : "Supported network"}</Operators>
              </div>
            </CountryCard>
          ))}
          {filteredCountries.length === 0 && <NoResults>No countries match your search.</NoResults>}
        </CountryList>
      </ModalLayout>
    </StyledModal>
  );
}
